import { useRef, useState, useCallback } from "react";
import { Modal, Alert, Button, Spin } from "antd";
import { Html5Qrcode } from "html5-qrcode";

interface UrlQRScannerModalProps {
  visible: boolean;
  onClose: () => void;
  onUrlScan: (url: string) => void;
}

export default function UrlQRScannerModal({
  visible,
  onClose,
  onUrlScan,
}: UrlQRScannerModalProps) {
  const [error, setError] = useState<string | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const scannerRef = useRef<Html5Qrcode | null>(null);

  const stopScanner = useCallback(async () => {
    if (scannerRef.current) {
      try {
        const scanner = scannerRef.current;
        scannerRef.current = null;
        if (scanner.isScanning) {
          await scanner.stop();
        }
      } catch (err) {
        console.error("Error stopping scanner:", err);
      }
    }
    setIsScanning(false);
    setIsStarting(false);
  }, []);

  const handleScanResult = useCallback(
    (decodedText: string) => {
      const candidate = decodedText.trim();
      try {
        const parsed = new URL(candidate);
        if (!parsed.protocol.startsWith("http")) {
          setError("QR neobsahuje platný http/https odkaz.");
          return;
        }
        void stopScanner();
        onUrlScan(parsed.toString());
      } catch {
        setError("QR neobsahuje platný URL odkaz.");
      }
    },
    [onUrlScan, stopScanner]
  );

  const startScanner = useCallback(async () => {
    if (scannerRef.current) return;

    const element = document.getElementById("qr-reader-url");
    if (!element) return;

    setError(null);
    setIsStarting(true);

    try {
      const scanner = new Html5Qrcode("qr-reader-url");
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => handleScanResult(decodedText),
        () => {}
      );
      setIsScanning(true);
    } catch (err) {
      console.error("QR Scanner error:", err);
      setError("Nepodařilo se spustit kameru. Zkontrolujte oprávnění.");
      scannerRef.current = null;
    } finally {
      setIsStarting(false);
    }
  }, [handleScanResult]);

  const handleAfterOpenChange = (open: boolean) => {
    if (open) {
      setTimeout(() => {
        startScanner();
      }, 300);
    }
  };

  const handleClose = () => {
    void stopScanner();
    onClose();
  };

  return (
    <Modal
      title="Skenovat QR odkaz"
      open={visible}
      onCancel={handleClose}
      afterOpenChange={handleAfterOpenChange}
      footer={<Button onClick={handleClose}>Zavřít</Button>}
      destroyOnClose
      width={350}
      centered
    >
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
        {error && (
          <Alert
            type="error"
            message={error}
            showIcon
            closable
            onClose={() => setError(null)}
            style={{ width: "100%" }}
          />
        )}
        <div
          id="qr-reader-url"
          style={{
            width: "100%",
            maxWidth: 300,
            minHeight: 300,
            background: "#000",
            borderRadius: 8,
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isStarting && !isScanning && <Spin size="large" />}
        </div>
      </div>
    </Modal>
  );
}
